import { Dispatch, SetStateAction } from "react"

import { ProductSheetLanguages } from "@tfg/types/constants/language.constants"
import {
  ComponentContent,
  ComponentType,
} from "@tfg/types/schemas/communication-studio/components"
import { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import {
  hasValidationErrors,
  getValidationErrorMessages,
  ComponentValidationResults,
  useGetComponentRenderer,
} from "~/pages/Brand/CommunicationStudio/Content/config/component-renderer"

export interface ComponentTypeRendererProps {
  componentType: ComponentType
  textContent: ComponentContent | null
  setTextContent: Dispatch<SetStateAction<ComponentContent | null>>
  updateTextContent: (
    blockIndex: number,
    textIndex: number,
    newValue: string
  ) => void
  configurations?: Partial<ContentFilteringProps> | null
  setConfigurations?: Dispatch<
    SetStateAction<Partial<ContentFilteringProps> | null>
  >
  validations?: ComponentValidationResults | null
}

export const ComponentTypeRenderer = ({
  componentType,
  textContent,
  setTextContent,
  updateTextContent,
  configurations,
  setConfigurations,
  validations: passedValidations,
}: ComponentTypeRendererProps) => {
  const rendererMetadata = useGetComponentRenderer(componentType)
  if (!rendererMetadata) {
    console.warn(`No renderer found for component type: ${componentType}`)
    return null
  }

  const { renderer: RendererComponent, propsMapper } = rendererMetadata

  // Use validations passed from parent (ComponentModal) instead of re-validating
  // This prevents duplicate validation and ensures single source of truth
  const validations = passedValidations || {
    content: undefined,
    configurations: undefined,
  }

  if (process.env.NODE_ENV !== "prod") {
    if (validations.content && hasValidationErrors(validations.content)) {
      console.warn(
        `Content validation errors for ${componentType}:`,
        getValidationErrorMessages(validations.content.errors)
      )
    }
    if (
      validations.configurations &&
      hasValidationErrors(validations.configurations)
    ) {
      console.warn(
        `Configuration validation errors for ${componentType}:`,
        getValidationErrorMessages(validations.configurations.errors)
      )
    }
  }

  const commonProps = {
    textContent,
    setTextContent,
    updateTextContent,
    componentType,
    language: ProductSheetLanguages.EN,
    configurations,
    setConfigurations,
  }

  const rendererProps = propsMapper
    ? propsMapper(commonProps, validations, rendererMetadata)
    : {
        ...commonProps,
        contentValidationErrors: validations.content?.errors,
        configurationsValidationErrors: validations.configurations?.errors,
      }

  return <RendererComponent {...rendererProps} />
}
